/**
 * Global error boundary (REN-21).
 *
 * If the page-level render throws, or any later `window.onerror` /
 * `unhandledrejection` fires, we replace the target container with a plain,
 * on-brand fallback message and a reload link. The real error is logged to
 * the browser console for developers; nothing about the stack, file path,
 * or exception message reaches the DOM. That keeps us safe from leaking
 * internal filenames, build hashes, or user input reflected back through an
 * error string.
 *
 * `install` is idempotent — calling it from every page entry is fine.
 */

let installed = false;
let boundaryContainer: HTMLElement | null = null;
let showing = false;

export function install(container: HTMLElement): void {
  boundaryContainer = container;
  if (installed) return;
  installed = true;

  const handle = (source: string, err: unknown): void => {
    // Log the actual error for developers only — never surface it.
    // eslint-disable-next-line no-console
    console.error(`[rentorown] ${source}:`, err);
    showFallback();
  };

  window.addEventListener("error", (event) => {
    handle("window.error", event.error ?? event.message);
  });

  window.addEventListener("unhandledrejection", (event) => {
    handle("unhandledrejection", event.reason);
  });
}

/**
 * Run `fn` and, if it throws, show the fallback UI. Returns the fn's return
 * value on success and `undefined` on failure. Use to wrap the initial
 * synchronous render call.
 */
export function guard<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[rentorown] initial render:", err);
    showFallback();
    return undefined;
  }
}

/** Exported for tests. */
export function showFallback(): void {
  if (showing) return;
  showing = true;
  const target = boundaryContainer ?? document.body;
  if (!target) return;
  target.innerHTML = "";
  const wrap = document.createElement("section");
  wrap.setAttribute("role", "alert");
  wrap.className = "error-boundary";
  wrap.innerHTML = [
    '<h1 class="error-boundary-title">Something went wrong.</h1>',
    '<p class="error-boundary-body">The calculator hit an unexpected snag. Reloading usually fixes it. If it keeps happening, the error has been logged in your browser\'s developer console.</p>',
    '<p class="error-boundary-actions"><a class="error-boundary-cta" href="/">Reload the calculator</a></p>',
  ].join("");
  target.append(wrap);
}

/** Exported for tests — resets module-level state. */
export function _reset(): void {
  installed = false;
  boundaryContainer = null;
  showing = false;
}
